import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, XCircle, Clock3, Package, ShieldCheck, Mail, ScrollText, AlertTriangle, Truck, Wallet, HelpCircle } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { RETURNS_DAYS_DEFAULT } from "@/lib/site-config";

const SITE_URL = "https://fixhen.com";
const PAGE_URL = `${SITE_URL}/returns`;
const RETURN_DAYS = RETURNS_DAYS_DEFAULT;
const CONTACT_EMAIL = "info@contact.fixhen.com";

const FAQS: { q: string; a: string }[] = [
  {
    q: `How many days do I have to request a return?`,
    a: `You have ${RETURN_DAYS} calendar days from the date you (or a person you designate) take physical possession of the goods to request a return by contacting ${CONTACT_EMAIL} with your order number, the item(s) concerned, and photographs of the parts and their original packaging.`,
  },
  {
    q: `Who pays the return shipping cost?`,
    a: `Return shipping is at the customer's expense unless the item is confirmed by FIXHEN to be defective, incorrectly supplied, or damaged in transit. In those cases we arrange a prepaid label or reimburse a documented, reasonable carrier cost.`,
  },
  {
    q: `Can I return an installed, painted, machined or fitted part?`,
    a: `No. Once a part has been installed, test-fitted with force, painted, drilled, cut, machined, heat-cycled, or otherwise modified from its as-delivered state, it is deemed used and is no longer eligible for return under our voluntary policy or under statutory withdrawal rights (Art. 16 CRD).`,
  },
  {
    q: `Are custom, made-to-order or vehicle-specific parts returnable?`,
    a: `No. Parts manufactured, cut, printed, or configured to your specification — including bespoke 3D-printed items, custom colours, engraved parts, and made-to-order sets — are excluded from the right of withdrawal under Article 16(c) of Directive 2011/83/EU.`,
  },
  {
    q: `How long does a refund take?`,
    a: `Once the returned goods arrive and pass inspection (typically within 3 business days of receipt), we issue the refund within 14 days using the original payment method. We may withhold the refund until we have received the goods back or you have supplied proof of return, whichever is earlier.`,
  },
  {
    q: `What if my part arrives damaged or the wrong item was sent?`,
    a: `Notify us within 48 hours of delivery at ${CONTACT_EMAIL} with photographs of the outer packaging, the inner packaging and the part. We will, at our sole discretion, replace the item, ship the correct part, or issue a full refund including the original shipping charge.`,
  },
  {
    q: `Do you offer exchanges?`,
    a: `We do not operate a direct exchange programme. Return the original item under this policy and place a new order for the desired part; this keeps stock, pricing and warranty tracking clean for both sides.`,
  },
];

export const Route = createFileRoute("/returns")({
  head: () => {
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "MerchantReturnPolicy",
        name: "FIXHEN 14-Day Return Policy",
        url: PAGE_URL,
        inStoreReturnsOffered: false,
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: RETURN_DAYS,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/ReturnShippingFees",
        applicableCountry: [
          "BG","AT","BE","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
        ],
        refundType: "https://schema.org/FullRefund",
        itemCondition: "https://schema.org/NewCondition",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Returns", item: PAGE_URL },
        ],
      },
    ];
    return {
      meta: [
        { title: "Returns Policy — 14-Day Right of Withdrawal | FIXHEN" },
        {
          name: "description",
          content: `FIXHEN offers a ${RETURN_DAYS}-day return window on unused, unfitted parts in original packaging. Read the full policy, exclusions, refund timeline and how to start a return.`,
        },
        { property: "og:title", content: "Returns Policy — 14-Day Right of Withdrawal | FIXHEN" },
        {
          property: "og:description",
          content: `Clear ${RETURN_DAYS}-day return window, EU-compliant, with a fair inspection process. Learn what is returnable and how refunds work at FIXHEN.`,
        },
        { property: "og:type", content: "article" },
        { property: "og:url", content: PAGE_URL },
        { name: "twitter:title", content: "Returns Policy — 14-Day Right of Withdrawal | FIXHEN" },
        {
          name: "twitter:description",
          content: `FIXHEN's ${RETURN_DAYS}-day returns: clear, EU-compliant, and fair.`,
        },
        { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
        { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },
      ],
      links: [{ rel: "canonical", href: PAGE_URL }],
      scripts: jsonLd.map((data) => ({
        type: "application/ld+json",
        children: JSON.stringify(data),
      })),
    };
  },
  component: ReturnsPage,
});

function ReturnsPage() {
  const lastUpdated = "1 July 2026";
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:py-16">
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li><Link to="/" className="hover:text-foreground">Home</Link></li>
            <li aria-hidden>/</li>
            <li className="text-foreground">Returns</li>
          </ol>
        </nav>

        <header className="mb-10 border-b border-border pb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> Legal · Returns & Refunds
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-foreground sm:text-5xl">
            Returns Policy
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            A clear, fair and legally-compliant <strong className="text-foreground">{RETURN_DAYS}-day</strong> right of return
            for parts purchased from FIXHEN. Read it carefully before ordering — placing an
            order confirms your acceptance of this policy.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">Last updated: {lastUpdated}</p>
        </header>

        <section className="mb-10 grid gap-3 sm:grid-cols-3">
          <SummaryCard icon={<Clock3 className="h-5 w-5" />} title={`${RETURN_DAYS} days`} body="From date of delivery to notify us in writing." />
          <SummaryCard icon={<Package className="h-5 w-5" />} title="Unused only" body="Original, unopened packaging; no install marks." />
          <SummaryCard icon={<Wallet className="h-5 w-5" />} title="Refund in 14 days" body="After we receive & inspect the returned goods." />
        </section>

        <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-black prose-headings:tracking-tight prose-h2:mt-12 prose-h2:text-2xl prose-h3:text-lg prose-a:text-primary prose-strong:text-foreground">
          <h2 id="scope"><ScrollText className="mr-2 inline h-5 w-5" />1. Scope & legal basis</h2>
          <p>
            This policy governs the return of goods purchased through <strong>fixhen.com</strong>
            (the "Site") operated by FIXHEN (the "Seller", "we", "our"). It applies to consumers
            resident in the European Union and, on a voluntary contractual basis with identical terms,
            to customers resident elsewhere. For EU consumers, it complements — and where more
            favourable, prevails over — the statutory right of withdrawal under Articles 9–16 of
            Directive 2011/83/EU on Consumer Rights, and the two-year legal guarantee of conformity
            under Directive (EU) 2019/771. Nothing in this policy limits or excludes any statutory
            right you have that cannot be lawfully limited or excluded.
          </p>
          <p>
            Business customers (B2B), professional installers and resellers act under separate
            commercial terms; the withdrawal right described below does <strong>not</strong> apply
            to them, and returns are accepted only where expressly agreed in writing in advance.
          </p>

          <h2 id="window"><Clock3 className="mr-2 inline h-5 w-5" />2. The {RETURN_DAYS}-day return window</h2>
          <p>
            You may withdraw from your purchase without giving any reason within{" "}
            <strong>{RETURN_DAYS} calendar days</strong> starting on the day after you (or a third
            party you designate, other than the carrier) take physical possession of the goods. For
            multi-item orders shipped separately, the period starts on delivery of the last item.
          </p>
          <p>
            To meet the deadline, it is sufficient to send your written withdrawal notice to
            <a href={`mailto:${CONTACT_EMAIL}`}> {CONTACT_EMAIL}</a> before the {RETURN_DAYS}-day period
            expires. Notice must include: order number, item SKU(s), quantity, reason (optional),
            and photographs of the goods and their packaging in current condition. Verbal or
            social-media notifications are not accepted as valid withdrawal notice.
          </p>

          <h2 id="condition"><Package className="mr-2 inline h-5 w-5" />3. Condition of returned goods</h2>
          <p>
            To qualify for a full refund, returned items must be:
          </p>
          <ul>
            <li>Unused, unfitted, unmodified and free of any signs of installation, wear, dirt, oil, adhesive or heat exposure;</li>
            <li>In their <strong>original, undamaged</strong> manufacturer packaging, with all labels, seals, tags, protective films, hardware, fasteners, instructions and accessories;</li>
            <li>Accompanied by proof of purchase (order number or invoice).</li>
          </ul>
          <p>
            You are liable for any diminished value of the goods resulting from handling other
            than what is necessary to establish their nature, characteristics and functioning
            (Article 14(2) CRD). In practice this means you may inspect a part as you would in
            a shop — you may not install, test-run, machine, drill, cut, paint, heat-cycle or
            otherwise use it. Where diminished value is established, we are entitled to deduct
            up to 100% of the item price from the refund and, where the item has become
            unsaleable, to refuse the return.
          </p>

          <h2 id="excluded"><XCircle className="mr-2 inline h-5 w-5" />4. Non-returnable items</h2>
          <p>The following are <strong>excluded</strong> from the {RETURN_DAYS}-day right of withdrawal and from voluntary returns, in line with Article 16 of Directive 2011/83/EU:</p>
          <ul>
            <li><strong>Custom & made-to-order parts:</strong> items manufactured, printed, cut, engraved, painted or configured to your specification, including bespoke 3D-printed components and vehicle-specific sets prepared on request;</li>
            <li><strong>Installed or fitted parts:</strong> anything showing evidence of test-fitting under force, mounting, torqueing, gluing, sealing or connection to a vehicle;</li>
            <li><strong>Electrical & electronic components</strong> once unsealed, energised, coded, flashed or paired to a VIN/ECU;</li>
            <li><strong>Fluids, lubricants, sealants, adhesives, paints and consumables</strong> once the tamper-evident seal is broken;</li>
            <li><strong>Safety-critical items</strong> (airbags, seatbelt tensioners, pyrotechnic components) for which resale would breach applicable regulations;</li>
            <li><strong>Gift cards, digital downloads and service fees;</strong></li>
            <li>Any item whose original packaging has been destroyed, defaced or written on.</li>
          </ul>

          <h2 id="process"><Truck className="mr-2 inline h-5 w-5" />5. How to return an item</h2>
          <ol>
            <li><strong>Notify us</strong> by email at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within {RETURN_DAYS} days of delivery. Include order number, SKU(s) and photos.</li>
            <li><strong>Wait for a Return Merchandise Authorisation (RMA)</strong> number. Returns received without a valid RMA may be refused or held at the customer's cost.</li>
            <li><strong>Pack the item</strong> in its original packaging, placed inside a sturdy outer carton. Do not affix labels or tape directly to the manufacturer box.</li>
            <li><strong>Ship the parcel</strong> to the address supplied with your RMA, using a tracked and insured service. Risk of loss and damage in transit remains with you until we sign for the parcel.</li>
            <li><strong>Retain proof of postage</strong> until the refund is settled.</li>
          </ol>

          <h2 id="costs"><Wallet className="mr-2 inline h-5 w-5" />6. Return shipping costs</h2>
          <p>
            <strong>On delivery you are never charged customs duty</strong> — every order ships
            Delivered Duty Paid, so import duties, VAT, brokerage and clearance are already settled
            by FIXHEN before the parcel leaves our warehouse (see our{" "}
            <Link to="/shipping">Shipping Policy</Link>). Where the return is due to a change of
            mind, ordering the wrong part, or any reason not attributable to the Seller,{" "}
            <strong>the customer bears the direct cost of returning the goods</strong> — that is,
            the return carrier charge and any storage or handling fees charged by the carrier; no
            customs duty is charged to you. Where the return is due to a confirmed defect, a wrong
            item shipped, or transport damage documented within 48 hours of delivery, FIXHEN
            issues a prepaid label or reimburses the actual, documented, reasonable carrier cost.
          </p>
          <p>
            The <strong>original outbound shipping</strong> charge is refunded only where you
            return the entire order and where the return is attributable to us. Otherwise, only
            the price of the returned item(s) is refunded, and the original shipping fee is
            retained.
          </p>

          <h2 id="refund"><CheckCircle2 className="mr-2 inline h-5 w-5" />7. Refunds</h2>
          <p>
            Once the returned goods arrive and pass inspection, we issue the refund within{" "}
            <strong>14 days</strong> using the same means of payment you used for the original
            transaction, unless you have expressly agreed otherwise; in any event you will not
            incur any fees as a result of the reimbursement. Where the goods have not yet been
            received and you cannot provide proof of return, we may lawfully withhold the
            reimbursement until either occurs, whichever is earlier (Article 13(3) CRD).
          </p>
          <p>
            Refunds are net of: (i) any diminished-value deduction under section 3; (ii) return
            shipping costs where you have requested us to arrange collection; and (iii) any
            supplementary delivery costs you chose beyond our standard delivery option.
          </p>

          <h2 id="damage"><AlertTriangle className="mr-2 inline h-5 w-5" />8. Damaged, defective or incorrect items</h2>
          <p>
            Inspect every parcel on delivery. Report any visible damage or missing/incorrect
            items to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within{" "}
            <strong>48 hours</strong> of receipt, with photographs of: (a) the outer packaging
            before opening, (b) the inner packaging, and (c) the item itself. Claims submitted
            after 48 hours may be treated as post-delivery damage and refused.
          </p>
          <p>
            Where a defect covered by the two-year legal guarantee (Directive (EU) 2019/771)
            manifests within the guarantee period, we will — at our option and free of charge —
            repair, replace, or refund the item, in that order of priority, subject to the
            statutory conditions and the item having been correctly stored, installed and used.
          </p>

          <h2 id="abuse"><ShieldCheck className="mr-2 inline h-5 w-5" />9. Anti-abuse & warranty voidance</h2>
          <p>
            To protect honest customers and the integrity of our supply chain, we reserve the
            right to refuse returns or void warranty where we identify: (a) tampered
            serial numbers or removed markings; (b) parts substituted for a different item;
            (c) evidence of use in motorsport, off-road, commercial fleet or non-approved
            applications; (d) repeated returns suggestive of "wardrobing" or resale activity;
            or (e) any breach of these terms. Our determination, made in good faith and
            supported by photographic and technical evidence, is final subject to your
            statutory rights and any competent dispute-resolution body.
          </p>

          <h2 id="withdrawal"><ScrollText className="mr-2 inline h-5 w-5" />10. Model withdrawal form</h2>
          <p>
            You may — but are not required to — use the following model form (Annex I(B),
            Directive 2011/83/EU):
          </p>
          <blockquote className="not-prose rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm leading-relaxed text-muted-foreground">
            <p className="font-semibold text-foreground">To: FIXHEN — {CONTACT_EMAIL}</p>
            <p className="mt-2">
              I/We [*] hereby give notice that I/We [*] withdraw from my/our [*] contract of sale of
              the following goods [*]: __________<br />
              Ordered on [*] / received on [*]: __________<br />
              Name of consumer(s): __________<br />
              Address of consumer(s): __________<br />
              Signature of consumer(s) (only if this form is notified on paper): __________<br />
              Date: __________<br />
              <em>[*] Delete as appropriate.</em>
            </p>
          </blockquote>

          <h2 id="law"><ScrollText className="mr-2 inline h-5 w-5" />11. Governing law & dispute resolution</h2>
          <p>
            This policy is governed by the laws of the Republic of Bulgaria, without prejudice to
            the mandatory consumer-protection provisions of the country of your habitual residence
            within the EU. Disputes may be submitted to the competent courts of Sofia, Bulgaria,
            or, at the consumer's option, to the courts of their habitual residence. Consumers may
            also use the European Commission's Online Dispute Resolution platform at{" "}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">
              ec.europa.eu/consumers/odr
            </a>.
          </p>

          <h2 id="faq"><HelpCircle className="mr-2 inline h-5 w-5" />12. Frequently asked questions</h2>
          <div className="not-prose space-y-3">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-lg border border-border bg-card p-4 open:shadow-sm">
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground marker:hidden">
                  <span className="mr-2 text-primary group-open:hidden">+</span>
                  <span className="mr-2 hidden text-primary group-open:inline">−</span>
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>

          <h2 id="contact"><Mail className="mr-2 inline h-5 w-5" />13. Contact</h2>
          <p>
            Returns team — <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Response time:
            one business day, Monday to Friday, excluding Bulgarian public holidays.
          </p>
        </article>

        <aside className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-6">
          <h3 className="text-lg font-bold text-foreground">Ready to start a return?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Email <a className="font-medium text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}?subject=Return%20request`}>{CONTACT_EMAIL}</a> with your order number and photos. We'll reply within one business day with your RMA.
          </p>
        </aside>
      </main>
      <SiteFooter />
    </div>
  );
}

function SummaryCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-primary">{icon}<span className="text-sm font-bold text-foreground">{title}</span></div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}